import { fetchWithServerErrorToast } from "../api";

const validationStates = new WeakMap();

function getFeedbackElement(input) {
  const sibling = input.nextElementSibling;
  if (sibling instanceof HTMLElement && sibling.classList.contains("invalid-feedback")) {
    return sibling;
  }

  return null;
}

function applyValidationState(input, message) {
  const feedback = getFeedbackElement(input);
  const normalizedMessage = String(message ?? "").trim();

  input.setCustomValidity(normalizedMessage);
  input.classList.toggle("is-invalid", normalizedMessage !== "");

  if (normalizedMessage === "") {
    input.classList.remove("is-valid");
  }

  if (feedback) {
    feedback.textContent = normalizedMessage || input.dataset.invalidMessage || "Nieprawidłowa wartość";
  }

  input.dispatchEvent(new Event("student-identifier-validation"));
}

async function runIdentifierValidation(input) {
  const baseUrl = String(input.dataset.studentIdentifierCheckUrl ?? "").trim();
  if (baseUrl === "") {
    return "";
  }

  const identifier = String(input.value ?? "").trim();
  const excludeId = String(input.dataset.studentIdentifierExcludeId ?? "0").trim();

  if (identifier === "") {
    return "Identyfikator ucznia jest wymagany.";
  }

  if (identifier.length > 64) {
    return "Identyfikator ucznia może mieć maksymalnie 64 znaki.";
  }

  const requestUrl = new URL(baseUrl, window.location.origin);
  requestUrl.searchParams.set("identifier", identifier);
  if (excludeId !== "" && excludeId !== "0") {
    requestUrl.searchParams.set("exclude_student_id", excludeId);
  }

  try {
    const response = await fetchWithServerErrorToast(requestUrl.toString(), {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });
    const payload = await response.json();

    return payload?.valid ? "" : String(payload?.message ?? "Nieprawidłowa wartość");
  } catch {
    return "";
  }
}

export function initStudentIdentifierValidation(root = document) {
  const inputs = Array.from(root.querySelectorAll("[data-student-identifier-check-url]"));
  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement) || input.dataset.studentIdentifierValidationInit === "1") {
      return;
    }

    input.dataset.studentIdentifierValidationInit = "1";

    let debounceId = 0;
    let requestVersion = 0;
    let checkedValue = null;
    const state = {
      pending: false,
      reset() {
        window.clearTimeout(debounceId);
        requestVersion += 1;
        checkedValue = null;
        state.pending = false;
        applyValidationState(input, "");
      },
    };
    validationStates.set(input, state);

    const scheduleValidation = () => {
      const identifier = String(input.value ?? "").trim();
      if (state.pending || checkedValue === identifier) {
        return;
      }

      if (input.readOnly || (input.hasAttribute("data-duplicate-identifier-original")
        && identifier === String(input.dataset.duplicateIdentifierOriginal ?? "").trim())) {
        state.reset();
        return;
      }

      state.pending = true;
      input.dispatchEvent(new Event("student-identifier-validation"));
      const version = requestVersion;
      debounceId = window.setTimeout(async () => {
        const message = await runIdentifierValidation(input);
        if (version !== requestVersion) {
          return;
        }

        checkedValue = identifier;
        state.pending = false;
        applyValidationState(input, message);
      }, 250);
    };

    input.addEventListener("input", () => {
      state.reset();
      scheduleValidation();
    });
    input.addEventListener("blur", scheduleValidation);
  });

  initDuplicateIdentifierRepairForms(root);

  return inputs.length > 0;
}

function initDuplicateIdentifierRepairForms(root = document) {
  const scope = root instanceof Element ? root : document;

  scope.querySelectorAll('form[data-duplicate-identifier-repair="1"]').forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.duplicateIdentifierRepairInit === "1") {
      return;
    }

    form.dataset.duplicateIdentifierRepairInit = "1";
    const inputs = Array.from(form.querySelectorAll("input[data-duplicate-identifier-original]"));
    const submitButton = form.querySelector('button[type="submit"]');
    let activeInput = null;

    const updateSubmitState = () => {
      const changedCount = inputs.filter(
        (input) => String(input.value ?? "").trim() !== String(input.dataset.duplicateIdentifierOriginal ?? "").trim(),
      ).length;

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = changedCount !== 1 || inputs.some(
          (input) => !input.validity.valid || validationStates.get(input)?.pending,
        );
      }
    };

    const activateInput = (selectedInput) => {
      activeInput = selectedInput;
      inputs.forEach((input) => {
        const inactive = input !== selectedInput;
        input.readOnly = inactive;
        input.classList.toggle("bg-body-secondary", inactive);
        if (inactive) {
          input.value = input.dataset.duplicateIdentifierOriginal ?? "";
          validationStates.get(input)?.reset();
        }
      });
      updateSubmitState();
    };

    inputs.forEach((input) => {
      input.addEventListener("focus", () => {
        if (activeInput !== null && activeInput !== input) {
          activateInput(input);
        }
      });
      input.addEventListener("input", () => activateInput(input));
      input.addEventListener("student-identifier-validation", updateSubmitState);
    });
    form.closest(".modal")?.addEventListener("hidden.bs.modal", () => {
      activeInput = null;
      inputs.forEach((input) => {
        input.value = input.dataset.duplicateIdentifierOriginal ?? "";
        input.readOnly = false;
        input.classList.remove("bg-body-secondary");
        validationStates.get(input)?.reset();
      });
      updateSubmitState();
    });
    form.addEventListener("submit", (event) => {
      updateSubmitState();
      if (!(submitButton instanceof HTMLButtonElement) || submitButton.disabled) {
        event.preventDefault();
      }
    });
    updateSubmitState();
  });
}
